import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import ProfileDropdown from '../dropdowns/profileDropdown';
import { NavbarMain, EmptyDiv, Content, BottomSection } from './styles';
import { isMediaRecordingSupported } from '../../helpers/common';
import NavbarRecording from './recordingNavbar';
import { useMobileDevice } from '../../global/useMobile';
import { rootStore } from '../../mobx/rootStore';
import HelpSupportDropdown from './helpSupportDropdown';
import FavoriteProjects from './favoriteProjects';
import TimerSection from './timerSection';
import NavbarMenuItems from './menuItems';
import Modal from '../modals/modal';
import PlanUpgradeModel from '../modals/planUpgradeModal';
import ResponsiveHeader from '../responsiveHeader';
import PlanUpgradeModelStep1 from '../modals/planUpgradeModalStep1';

// Compute hostname once outside component to avoid repeated checks
const isEmbedHostname = window.location.hostname.startsWith('embed');

type ModalState = { type: 'none' } | { type: 'planUpgrade' } | { type: 'planUpgradeStep' };

interface Props {
  setOpenContactUsModal: () => void;
}

interface NavbarBodyProps {
  isMobile: boolean;
  setOpenContactUsModal: () => void;
  onClickOpenModel: () => void;
}

const NavbarBody: React.FC<NavbarBodyProps> = observer(({ isMobile, setOpenContactUsModal, onClickOpenModel }) => {
  const { timeTrackingStore } = rootStore;
  const { activeTimeLogs } = timeTrackingStore || {};
  const hasActiveTimeLogs = activeTimeLogs?.length > 0;

  return (
    <NavbarMain className={`navbar${hasActiveTimeLogs ? ' timer-navbar' : ''}`}>
      <div>
        {!isMobile ? <ProfileDropdown /> : <ResponsiveHeader title='Home' noMargin={true} />}

        <NavbarMenuItems />
        <Content isTrackingActivity={hasActiveTimeLogs} className={hasActiveTimeLogs ? 'navbar-tracker' : ''}>
          <FavoriteProjects isTrackingActivity={hasActiveTimeLogs} />
          {!isMobile && (
            <BottomSection className='navbar-bottom'>
              {isMediaRecordingSupported() && <NavbarRecording />}
              <TimerSection />
              <HelpSupportDropdown setOpenContactUsModal={setOpenContactUsModal} onClickOpenModel={onClickOpenModel} />
            </BottomSection>
          )}
        </Content>
      </div>
    </NavbarMain>
  );
});

const Navbar: React.FC<Props> = (props) => {
  const { setOpenContactUsModal } = props;

  //Mobx store variable
  const { appStore } = rootStore;
  const { isNavbarShow, isPlanUpgradeModelOpen } = appStore || {};

  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Sync MobX-driven plan upgrade modal into local state
  useEffect(() => {
    if (isPlanUpgradeModelOpen) {
      setModal({ type: 'planUpgrade' });
    }
  }, [isPlanUpgradeModelOpen]);

  //Other variable
  const isMobile = useMobileDevice();

  const clickOpenModel = () => {
    setModal({ type: 'planUpgrade' });
  };

  const clickCloseModel = () => {
    setModal({ type: 'none' });
    if (isPlanUpgradeModelOpen) {
      appStore.setIsPlanUpgradeModelOpen(false);
    }
  };

  const handleClickOpenPlanUpgradeModelStep = () => {
    setModal({ type: 'planUpgradeStep' });
  };

  const handleClickClosePlanUpgradeModelStep = () => {
    setModal({ type: 'none' });
  };

  return (
    <>
      {!isEmbedHostname && isNavbarShow ? (
        <NavbarBody
          isMobile={isMobile}
          setOpenContactUsModal={setOpenContactUsModal}
          onClickOpenModel={clickOpenModel}
        />
      ) : (
        <EmptyDiv isNavbarHide={!isNavbarShow}></EmptyDiv>
      )}
      {modal.type === 'planUpgrade' && (
        <Modal open={true} onClose={clickCloseModel} maxWidth='692px' isResponsiveMaxWidth={true}>
          <PlanUpgradeModel closeModel={clickCloseModel} onClickUpgrade={handleClickOpenPlanUpgradeModelStep} />
        </Modal>
      )}
      {modal.type === 'planUpgradeStep' && (
        <Modal open={true} maxWidth='692px' onClose={handleClickClosePlanUpgradeModelStep} isResponsiveMaxWidth={true}>
          <PlanUpgradeModelStep1 />
        </Modal>
      )}
    </>
  );
};

export default observer(Navbar);
